import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, ArrowRightIcon, Building2 } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessStore } from '../../core/stores/businessStore'
import { BUSINESS_TYPES, BUSINESS_TYPE_LABELS } from '../../core/models/business'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import Input from '../../components/ui/Input'
import Textarea from '../../components/ui/Textarea'
import Select from '../../components/ui/Select'
import SaveButton from '../../components/buttons/SaveButton'
import CancelButton from '../../components/buttons/CancelButton'
import { AppRoute } from '../../core/config/app-routes'
import toast from 'react-hot-toast'

export default function BusinessCreatePage() {
  const navigate = useNavigate()
  const { t, isRTL } = useTranslation()
  const { createBusiness } = useBusinessStore()

  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    description: '',
    address: '',
    contactNumber: '',
    businessType: '',
    color: '#1E293B'
  })

  const [errors, setErrors] = useState<{
    slug?: string
    name?: string
    address?: string
    contactNumber?: string
    businessType?: string
  }>({})

  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}

    if (!formData.slug.trim()) {
      newErrors.slug = t('fieldRequired') || 'This field is required'
    } else {
      // Validate slug format (lowercase, alphanumeric, hyphens only)
      const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
      if (!slugRegex.test(formData.slug.trim())) {
        newErrors.slug = 'Slug must contain only lowercase letters, numbers, and hyphens'
      }
    }

    if (!formData.name.trim()) {
      newErrors.name = t('fieldRequired') || 'This field is required'
    }

    if (!formData.address.trim()) {
      newErrors.address = t('fieldRequired') || 'This field is required'
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = t('fieldRequired') || 'This field is required'
    }

    if (!formData.businessType) {
      newErrors.businessType = t('fieldRequired') || 'This field is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(t('pleaseFillRequiredFields') || 'Please fill all required fields')
      return
    }

    setIsSubmitting(true)
    try {
      const newBusiness = await createBusiness({
        slug: formData.slug.trim(),
        name: formData.name,
        description: formData.description || undefined,
        address: formData.address,
        contactNumber: formData.contactNumber,
        businessType: formData.businessType as any,
        isActive: true,
        isAcceptingOrders: true,
        color: formData.color
      })

      if (newBusiness) {
        toast.success(t('businessCreatedSuccessfully') || 'Business created successfully')
        navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE + '/' + newBusiness.id)
      }
    } catch (error: any) {
      console.error('Failed to create business:', error)
      if (!error?.response) {
        toast.error(error.message || t('error') || 'Failed to create business')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(AppRoute.BUSINESS_OWNER_BUSINESSES_ROUTE)
  }

  const businessTypeOptions = BUSINESS_TYPES.map(type => ({
    value: type,
    label: BUSINESS_TYPE_LABELS[type] || type
  }))

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleCancel}
          className="px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          type="button"
        >
          {isRTL ? <ArrowRightIcon className="w-5 h-5" /> : <ArrowLeftIcon className="w-5 h-5" />}
        </button>
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-gray-700" />
          <h1 className="text-xl font-semibold text-gray-900">
            {t('createBusiness') || 'Create Business'}
          </h1>
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('basicInformation') || 'Basic Information'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Slug */}
            <div>
              <Input
                label={t('slug') || 'Slug'}
                type="text"
                value={formData.slug}
                onChange={(e) => {
                  // Convert to lowercase and replace spaces/spaces with hyphens
                  const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
                  handleInputChange('slug', value)
                }}
                placeholder={t('slug') || 'my-business-name'}
                error={errors.slug}
                helperText={t('slugHelperText') || 'URL-friendly identifier for your business (e.g., my-business-name)'}
                required
              />
            </div>

            {/* Business Name */}
            <div>
              <Input
                label={t('businessName')}
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('businessName') || 'Business Name'}
                error={errors.name}
                required
              />
            </div>

            {/* Description */}
            <div>
              <Textarea
                label={t('businessDescription')}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('businessDescription') || 'Business Description (Optional)'}
                rows={4}
              />
            </div>

            {/* Business Type */}
            <div>
              <Select
                label={t('businessType')}
                value={formData.businessType}
                onChange={(e) => handleInputChange('businessType', e.target.value)}
                options={businessTypeOptions}
                placeholder={t('businessType') || 'Select Business Type'}
                error={errors.businessType}
                required
              />
            </div>

            {/* Address */}
            <div>
              <Input
                label={t('businessAddress')}
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder={t('businessAddress') || 'Business Address'}
                error={errors.address}
                required
              />
            </div>

            {/* Contact Number and Business Color - Side by side on desktop, stacked on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Number */}
              <div>
                <Input
                  label={t('businessPhone')}
                  type="tel"
                  value={formData.contactNumber}
                  onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                  placeholder="01xxxxxxxxx"
                  error={errors.contactNumber}
                  required
                />
              </div>

              {/* Business Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('businessColor')}
                </label>
                <div className="flex items-start gap-2">
                  <div className="relative">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                      className="w-16 h-11 rounded-lg border-2 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="text"
                      value={formData.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                      placeholder="#1E293B"
                      helperText={t('businessColorHelperText')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <SaveButton
                onClick={handleSubmit}
                isLoading={isSubmitting}
                disabled={
                  !formData.slug.trim() ||
                  !formData.name.trim() ||
                  !formData.address.trim() ||
                  !formData.contactNumber.trim() ||
                  !formData.businessType ||
                  isSubmitting
                }
              />
              <CancelButton
                onClick={handleCancel}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
